// src/tabs/CompletudePanel.jsx — vue « CE QUI MANQUE » (admin).
//
// Répond à UNE question : qu'est-ce qui n'est pas rempli, et qu'est-ce que ça
// bloque ? Rien d'autre. Pas de saisie, pas d'enregistrement — on lit la config
// en mémoire et on renvoie l'admin vers l'écran qui sait la corriger.
//
// TOUTE la logique vit dans src/lib/config-completude.js (module PUR, testé).
// Ce fichier est de la PRÉSENTATION : s'il contenait une règle métier, elle
// serait intestable et divergerait du serveur.
//
// DA : primitives de l'espace Paramètres (_paramsUi) + tokens DS EXISTANTS
// uniquement. Aucune couleur nouvelle n'est introduite ici (règle Dimitri
// 2026-07-31 : toute couleur se valide à l'écran, donc on n'en invente pas).

import { useMemo, useState } from "react"
import { ClipboardCheck, AlertCircle, EyeOff, Info, CheckCircle2, ArrowRight } from "lucide-react"
import { DS } from "../lib/ui"
import { Section, FicheAlert, StatusPill } from "./_paramsUi"
import { analyserCompletude, grouperParCible, GRAVITE } from "../lib/config-completude"
import TaxeParcPanel from "./TaxeParcPanel"

// Habillage d'une gravité : libellé, ton de pastille (tons DS existants),
// icône lucide. Le SENS est porté par le module pur, pas ici.
const HABILLAGE = {
  [GRAVITE.BLOQUANT]: { label: "Bloquant", tone: "danger", Icon: AlertCircle },
  [GRAVITE.SILENCIEUX]: { label: "Silencieux", tone: "warning", Icon: EyeOff },
  [GRAVITE.INFORMATIF]: { label: "Informatif", tone: "info", Icon: Info },
}

// Compteur cliquable = filtre. Sobre : pas de menu, pas de tri configurable —
// trois chiffres, et on clique celui qu'on veut isoler.
function CompteurFiltre({ gravite, n, actif, onClick }) {
  const h = HABILLAGE[gravite]
  const Ico = h.Icon
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actif}
      data-testid={`completude-filtre-${gravite}`}
      className="fbz-press"
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        background: DS.card,
        border: `0.5px solid ${actif ? DS.borderHover : DS.border}`,
        borderRadius: DS.radiusControl,
        padding: "9px 13px", cursor: "pointer", fontFamily: DS.fontBody,
        opacity: actif ? 1 : 0.62,
      }}
    >
      <Ico size={15} strokeWidth={1.9} aria-hidden="true" style={{ color: DS.textSecondary }} />
      <span style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 17, color: DS.marineDark }}>{n}</span>
      <span style={{ fontSize: 12, color: DS.textSecondary, fontWeight: 500 }}>{h.label}</span>
    </button>
  )
}

// Une ligne = un manque. Libellé + gravité + ce que ça bloque + où le remplir.
function LigneManque({ m }) {
  const h = HABILLAGE[m.gravite]
  return (
    <div
      data-testid={`completude-manque-${m.id}`}
      style={{
        display: "flex", gap: 10, alignItems: "flex-start",
        padding: "10px 0", borderTop: `1px solid ${DS.border}`,
      }}
    >
      <StatusPill tone={h.tone} style={{ flex: "0 0 auto", marginTop: 1 }}>{h.label}</StatusPill>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: DS.textPrimary }}>
          {m.libelle}{" "}
          <code style={{ fontFamily: "monospace", fontSize: 11, color: DS.textMuted, fontWeight: 400 }}>
            {m.champ}
          </code>
        </div>
        <div style={{ fontSize: 12, color: DS.textSecondary, lineHeight: 1.5, marginTop: 3 }}>
          {m.bloque}
        </div>
        {m.note && (
          <div style={{ fontSize: 11.5, color: DS.textMuted, lineHeight: 1.5, marginTop: 3, fontStyle: "italic" }}>
            {m.note}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: DS.marine, marginTop: 5, fontWeight: 600 }}>
          <ArrowRight size={13} strokeWidth={2} aria-hidden="true" style={{ flex: "0 0 auto" }} />
          <span>{m.ou.onglet} › {m.ou.carte}</span>
        </div>
      </div>
    </div>
  )
}

/**
 * @param {object} props
 * @param {object} props.cfg — blob fbz8-cfg tel qu'il est en mémoire.
 * @param {Function} [props.save] — enregistre la config ; seule la saisie groupée
 *   des barèmes de taxe (TaxeParcPanel) s'en sert, après confirmation.
 * @param {object} [props.currentUser] — son email signe les barèmes saisis (saisi_par).
 */
export default function CompletudePanel({ cfg, save, currentUser }) {
  const [filtre, setFiltre] = useState(null) // null = tout
  const { manques, compteurs } = useMemo(() => analyserCompletude(cfg), [cfg])
  const visibles = filtre ? manques.filter((m) => m.gravite === filtre) : manques
  const groupes = useMemo(() => grouperParCible(visibles), [visibles])

  return (
    <>
    <Section
      tint="ardoise"
      icon={ClipboardCheck}
      title="Ce qui manque"
      subtitle="Les champs vides et ce qu'ils empêchent réellement. Lecture seule — la correction se fait sur l'écran indiqué."
    >
      {/* Ce que cet écran voit, et ce qu'il ne voit pas. Un écran d'audit qui
          laisserait croire qu'il est exhaustif serait pire qu'aucun écran. */}
      <FicheAlert tone="info" style={{ marginBottom: 14 }}>
        Analyse de la <strong>configuration des logements</strong> (nom et adresse des propriétaires,
        barème de taxe, caution, Stripe, lien avis, frais de ménage, capacité). Les réglages stockés
        hors de cette configuration — publication du site direct, politique d'annulation, modèles de
        messages — ne sont pas examinés ici.
      </FicheAlert>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
        {[GRAVITE.BLOQUANT, GRAVITE.SILENCIEUX, GRAVITE.INFORMATIF].map((g) => (
          <CompteurFiltre
            key={g}
            gravite={g}
            n={compteurs[g]}
            actif={filtre === null || filtre === g}
            onClick={() => setFiltre(filtre === g ? null : g)}
          />
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: DS.textMuted, marginBottom: 16 }}>
        <strong>Bloquant</strong> : quelque chose ne peut pas se produire du tout.{" "}
        <strong>Silencieux</strong> : ça marche, mais quelque chose disparaît sans le dire.{" "}
        <strong>Informatif</strong> : optionnel.
      </div>

      {manques.length === 0 && (
        <FicheAlert tone="success" testId="completude-vide">
          Rien ne manque : tous les champs contrôlés sont remplis.
        </FicheAlert>
      )}

      {manques.length > 0 && visibles.length === 0 && (
        <FicheAlert tone="success" testId="completude-filtre-vide">
          Aucun manque de cette gravité.
        </FicheAlert>
      )}

      {groupes.map((groupe) => (
        <div
          key={groupe.cle}
          data-testid={`completude-groupe-${groupe.cle}`}
          style={{
            border: `0.5px solid ${DS.border}`, borderRadius: DS.radiusCard,
            padding: "12px 14px 4px", marginBottom: 10, background: DS.card,
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 2 }}>
            <span style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 14, color: DS.marineDark }}>
              {groupe.titre}
            </span>
            {groupe.sousTitre && (
              <span style={{ fontSize: 11.5, color: DS.textMuted }}>{groupe.sousTitre}</span>
            )}
            <span style={{ fontSize: 10, letterSpacing: 1.1, textTransform: "uppercase", color: DS.textMuted, fontWeight: 700 }}>
              {groupe.portee}
            </span>
          </div>
          {groupe.manques.map((m) => <LigneManque key={m.id} m={m} />)}
        </div>
      ))}

      {manques.length > 0 && (
        <div style={{ display: "flex", gap: 7, alignItems: "flex-start", fontSize: 11.5, color: DS.textMuted, marginTop: 10, lineHeight: 1.5 }}>
          <CheckCircle2 size={14} strokeWidth={1.8} aria-hidden="true" style={{ flex: "0 0 auto", marginTop: 1 }} />
          <span>
            Cette liste se recalcule à chaque modification de la configuration : corrige sur l'écran
            indiqué, enregistre, et le manque disparaît d'ici.
          </span>
        </div>
      )}
    </Section>
    {/* La taxe de séjour de tout le parc, d'un coup — lecture et saisie groupée. */}
    <TaxeParcPanel cfg={cfg} save={save} currentUser={currentUser} />
    </>
  )
}
